import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, getJson, postJson, putJson } from './http.js';
import type {
  AdminMessages,
  AdminOrder,
  AdminPurchaseOrders,
  AdminSession,
  CampaignParams,
  OperationalMetrics,
} from './types.js';

const HTTP_UNAUTHORIZED = 401;
/** Production advances every few seconds; refresh the admin view without hammering the API. */
const ADMIN_REFRESH_MS = 15_000;
const SESSION_KEY = ['admin', 'session'];

/** Current admin session, or null when not logged in (401 is an answer, not an error). */
export function useAdminSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: async (): Promise<AdminSession | null> => {
      try {
        return await getJson<AdminSession>('/api/admin/session');
      } catch (error) {
        if (error instanceof ApiError && error.status === HTTP_UNAUTHORIZED) return null;
        throw error;
      }
    },
    retry: false,
  });
}

export function useAdminLogin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      postJson<AdminSession>('/api/admin/login', credentials),
    onSuccess: (session) => {
      client.setQueryData(SESSION_KEY, session);
    },
  });
}

export function useAdminLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => postJson<{ loggedOut: boolean }>('/api/admin/logout'),
    onSuccess: () => {
      client.removeQueries({ queryKey: ['admin'] });
      client.setQueryData(SESSION_KEY, null);
    },
  });
}

export function useAdminMetrics() {
  return useQuery({
    queryKey: ['admin', 'metrics'],
    queryFn: () => getJson<OperationalMetrics>('/api/admin/metrics'),
    refetchInterval: ADMIN_REFRESH_MS,
  });
}

export function useCampaign() {
  return useQuery({
    queryKey: ['admin', 'campaign'],
    queryFn: () => getJson<CampaignParams>('/api/admin/campaign'),
  });
}

export function useUpdateCampaign() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (params: CampaignParams) => putJson<CampaignParams>('/api/admin/campaign', params),
    onSuccess: (params) => {
      client.setQueryData(['admin', 'campaign'], params);
      void client.invalidateQueries({ queryKey: ['admin', 'metrics'] });
    },
  });
}

export function useAdminOrders() {
  return useQuery({
    queryKey: ['admin', 'orders'],
    queryFn: async () => (await getJson<{ orders: AdminOrder[] }>('/api/admin/orders')).orders,
    refetchInterval: ADMIN_REFRESH_MS,
  });
}

export function useAdminMessages() {
  return useQuery({
    queryKey: ['admin', 'messages'],
    queryFn: () => getJson<AdminMessages>('/api/admin/messages'),
    refetchInterval: ADMIN_REFRESH_MS,
  });
}

export function useAdminPurchaseOrders() {
  return useQuery({
    queryKey: ['admin', 'purchase-orders'],
    queryFn: () => getJson<AdminPurchaseOrders>('/api/admin/purchase-orders'),
    refetchInterval: ADMIN_REFRESH_MS,
  });
}
